<template>
  <BaseDialog
    contentClass="profile-field"
    ref="dialog"
    :show-actions="true"
    sidePanel
    white-content
    @closeModal="closeDialog"
  >
    <template slot="title">{{ titleText }}</template>
    <template slot="content">
      <form @submit.prevent="onSubmit" novalidate class="profile-field__form">
        <FormInput
          ref="titleInput"
          :label="$t('page.admin-tenant-profile.field-dialog.title-label')"
          :hint="$t('page.admin-tenant-profile.field-dialog.title-hint')"
          has-gray-background
          name="fieldTitle"
          isRequired
          v-model="title"
        />
        <FormInput
          :label="$t('page.admin-tenant-profile.field-dialog.aliases-label')"
          :hint="$t('page.admin-tenant-profile.field-dialog.aliases-hint')"
          has-gray-background
          name="fieldAliases"
          v-model="aliasesText"
        />
        <div class="profile-field__main-lang">
          <FormLabel>{{ $t('page.admin-tenant-profile.field-dialog.language-label') }}</FormLabel>
          <BaseSelect
            class="profile-field__main-lang-select"
            name="fieldLanguage"
            :items="mainLangOptions"
            item-text="name"
            item-value="code"
            :value="language"
            :placeholder="$t('page.admin-tenant-profile.field-dialog.language-label')"
            @change="onMainLanguageChange"
          />
        </div>
        <FormInput
          :label="$t('page.admin-tenant-profile.field-dialog.value-label')"
          :hint="$t('page.admin-tenant-profile.field-dialog.value-hint')"
          has-gray-background
          name="fieldValue"
          is-textarea
          isRequired
          v-model="value"
        />

        <div class="profile-field__languages">
          <FormLabel>{{ $t('page.admin-tenant-profile.field-dialog.other-languages-label') }}</FormLabel>
          <div v-for="(translation, index) in translations" :key="index" class="profile-field__lang-row">
            <BaseSelect
              class="profile-field__lang-select"
              :name="`fieldLang-${index}`"
              :items="optionsForRow(index)"
              item-text="name"
              item-value="code"
              :value="translation.language"
              :placeholder="$t('page.admin-tenant-profile.field-dialog.language-label')"
              @change="(code) => onLanguageChange(index, code)"
            />
            <FormInput
              class="profile-field__lang-value"
              has-gray-background
              isCompact
              :name="`fieldLangValue-${index}`"
              v-model="translation.value"
            />
            <BaseRoundButton
              :size="32"
              icon="trash-2"
              is-transparent
              is-red
              :tooltip-text="$t('actions.delete')"
              @click.native="removeTranslation(index)"
            />
          </div>
          <BaseButton v-if="canAddLanguage" is-small is-transparent has-icon-left @click.native="addTranslation">
            <BaseIcon name="plus" />
            {{ $t('page.admin-tenant-profile.field-dialog.add-language') }}
          </BaseButton>
        </div>

        <div class="profile-field__actions">
          <BaseButton is-large type="submit" :disabled="!canSubmit" :is-outlined="!canSubmit">
            {{ titleText }}
          </BaseButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import BaseSelect from '@/components/base/BaseSelect';
import FormInput from '@/components/form/FormInput.vue';
import FormLabel from '@/components/form/FormLabel.vue';
import { FIELD_LANGUAGES, fieldLanguageValues, localeToLang } from '@/util/tenant-profile';

export default {
  name: 'ProfileFieldDialog',
  components: { BaseButton, BaseDialog, BaseIcon, BaseRoundButton, BaseSelect, FormInput, FormLabel },
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      mode: 'add',
      id: null,
      categoryId: null,
      source: 'manual',
      title: '',
      aliasesText: '',
      value: '',
      // Language of the primary "value to fill".
      language: null,
      translations: [],
    };
  },
  computed: {
    titleText() {
      return this.mode === 'edit'
        ? this.$t('page.admin-tenant-profile.field-dialog.edit')
        : this.$t('page.admin-tenant-profile.field-dialog.new');
    },
    currentLang() {
      return localeToLang(this.$i18n.locale);
    },
    // The primary value's language can't be one already used by an
    // other-language row.
    mainLangOptions() {
      return FIELD_LANGUAGES.map((l) => ({
        ...l,
        disabled: this.translations.some((t) => t.language === l.code),
      }));
    },
    // Languages already taken by the field: the primary value's language plus
    // any already chosen in other-language rows.
    usedLanguages() {
      return [this.language, ...this.translations.map((t) => t.language)].filter(Boolean);
    },
    canAddLanguage() {
      return FIELD_LANGUAGES.some((l) => !this.usedLanguages.includes(l.code));
    },
    // Both the label and the value to fill are required before a field can be saved.
    canSubmit() {
      return !!this.title.trim() && !!this.value.trim();
    },
  },
  methods: {
    reset() {
      this.id = null;
      this.source = 'manual';
      this.title = '';
      this.aliasesText = '';
      this.value = '';
      this.language = this.currentLang;
      this.translations = [];
    },
    openAdd(categoryId) {
      this.mode = 'add';
      this.categoryId = categoryId;
      this.reset();
      this.$refs.dialog.openModal();
    },
    openEdit(entry) {
      this.mode = 'edit';
      this.id = entry.id;
      this.categoryId = entry.categoryId;
      this.source = entry.source;
      this.title = entry.title;
      this.aliasesText = (entry.aliases || []).join(', ');
      const pairs = fieldLanguageValues(entry);
      // One value: show it regardless of locale. Multiple: show the locale's.
      const main = pairs.length > 1 ? pairs.find((p) => p.language === this.currentLang) || pairs[0] : pairs[0];
      this.language = main ? main.language : this.currentLang;
      this.value = main ? main.value : '';
      this.translations = pairs
        .filter((p) => p.language !== this.language)
        .map((p) => ({ language: p.language, value: p.value }));
      this.$refs.dialog.openModal();
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
    },
    addTranslation() {
      const available = FIELD_LANGUAGES.find((l) => !this.usedLanguages.includes(l.code));
      this.translations.push({ language: available ? available.code : null, value: '' });
    },
    removeTranslation(index) {
      this.translations.splice(index, 1);
    },
    onMainLanguageChange(code) {
      this.language = code;
    },
    onLanguageChange(index, code) {
      this.translations[index].language = code;
    },
    // Disable the primary value's language and any already chosen in another
    // row, so a field can't hold two values for the same language.
    optionsForRow(index) {
      return FIELD_LANGUAGES.map((l) => ({
        ...l,
        disabled: l.code === this.language || this.translations.some((t, i) => i !== index && t.language === l.code),
      }));
    },
    onSubmit() {
      if (!this.canSubmit) return;
      const aliases = this.aliasesText
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean);
      // Keep only complete rows, de-duped by language (last one wins).
      const byLanguage = {};
      this.translations.forEach((t) => {
        if (t.language && t.value.trim()) byLanguage[t.language] = { language: t.language, value: t.value.trim() };
      });
      // The primary value owns its language; never duplicate it as a translation.
      delete byLanguage[this.language];
      const translations = Object.values(byLanguage);
      const entry = {
        id: this.id,
        categoryId: this.categoryId,
        source: this.source,
        title: this.title.trim(),
        aliases,
        value: this.value,
        language: this.language,
        translations,
      };
      this.$emit('submit', { mode: this.mode, entry });
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss">
.base-dialog__profile-field {
  .profile-field__actions {
    display: flex;
    justify-content: center;
    padding-top: $space-xl;
  }

  // Make disabled language options clearly non-selectable in the dropdown.
  .v-list-item--disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.profile-field {
  &__main-lang {
    display: flex;
    flex-direction: column;
    gap: $space-xs;
    padding: 12px 0;
  }

  // Shared styling so the primary-value language select and the other-language
  // selects look identical. Uses the default (square) BaseSelect style; only the
  // reserved label/details height is trimmed away.
  &__main-lang-select,
  &__lang-select {
    height: auto !important;

    .form-label,
    .v-text-field__details {
      display: none;
    }

    // Vuetify's v-text-field adds top padding/margins that push the control
    // down; strip them so the dropdown sits flush like the inputs.
    .v-input {
      margin: 0 !important;
      padding: 0 !important;
    }

    .v-input__slot {
      margin-bottom: 0 !important;
      min-height: 36px !important;
    }
  }

  &__main-lang-select {
    max-width: 220px;
  }

  &__languages {
    display: flex;
    flex-direction: column;
    gap: $space-sm;
  }

  &__lang-row {
    display: flex;
    align-items: center;
    gap: $space-sm;
  }

  &__lang-select {
    flex: 0 0 140px;
  }

  &__lang-value {
    flex: 1;
    min-width: 0;
    // Drop the compact wrapper padding and match the select height so the
    // input and dropdown align on the row.
    padding: 0 !important;

    .form-input__input {
      min-height: 36px !important;
    }
  }
}
</style>
